import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { deprecateComponent } from '@adsk/bim360-matrix-react-helpers/es/deprecate';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { v4 as getUUID } from 'uuid';

import './_DeprecatedTooltip.scss';

/**
 * A tooltip wrapper component
 * @param props
 * @returns {XML}
 * @constructor
 */
const DeprecatedTooltip = props => {
  deprecateComponent('DeprecatedTooltip', 'Tooltip', new Date(2020, 1, 22));
  const classes = classNames('Tooltip', props.className);
  const tooltipClasses = `DeprecatedTooltip__tooltip DeprecatedTooltip__tooltip--${props.theme}`;

  // if `id` is provided, tooltip node could be shared by different targets
  if (props.id) {
    return (
      <div className={classes} style={props.style}>
        <ReactTooltip
          class={tooltipClasses}
          place={props.place}
          effect="solid"
          disable={props.disable}
          id={props.id}
          delayShow={props.delayShow}
        >
          {props.text}
        </ReactTooltip>
      </div>
    );
  }

  const tooltipId = getUUID();
  const targetClasses = classNames('DeprecatedTooltip__target', props.childrenContainerClassName);
  return (
    <div className={classes} style={props.style}>
      <div className={targetClasses} data-tip={true} data-for={tooltipId}>
        {props.children}
      </div>
      <ReactTooltip
        class={tooltipClasses}
        effect="solid"
        place={props.place}
        disable={props.disable || !props.text}
        id={tooltipId}
        delayShow={props.delayShow}
      >
        {props.text}
      </ReactTooltip>
    </div>
  );
};

// rebuild all tooltips, see https://github.com/wwayne/react-tooltip#reacttooltiprebuild
DeprecatedTooltip.rebuild = ReactTooltip.rebuild;

DeprecatedTooltip.Themes = {
  DARK: 'dark',
  LIGHT: 'light'
};

DeprecatedTooltip.Places = {
  LEFT: 'left',
  TOP: 'top',
  RIGHT: 'right',
  BOTTOM: 'bottom'
};

DeprecatedTooltip.defaultProps = {
  theme: DeprecatedTooltip.Themes.DARK,
  place: DeprecatedTooltip.Places.TOP,
  style: {}
};

DeprecatedTooltip.propTypes = {
  /**`
   * Unique id for the tooltip,
   * if provided, the tooltip could be shared by different targets with the same `data-for`,
   * also specific props like `text`, `children`, `childrenContainerClassName` will be ignored
   */
  id: PropTypes.string,
  /**
   * Custom className for the component
   */
  className: PropTypes.string,
  /**
   * The text to display in the tooltip
   */
  text: PropTypes.node,
  /**
   * The element which is attached to the tooltip (the tooltip will be displayed when hovering this element)
   */
  children: PropTypes.node,
  /**
   * The styling theme for the tooltip
   */
  theme: PropTypes.oneOf(objectValues(DeprecatedTooltip.Themes)),
  /**
   * A className for container of the children element
   */
  childrenContainerClassName: PropTypes.string,
  /**
   * Whether to disable the display of the tooltip
   */
  disable: PropTypes.bool,
  /**
   * The placement location of the tooltip
   */
  place: PropTypes.oneOf(objectValues(DeprecatedTooltip.Places)),
  /**
   * Custom style
   */
  style: PropTypes.object,
  /**
   * Delay until showing up
   */
  delayShow: PropTypes.number
};

export default DeprecatedTooltip;
